<template>
  <div
    v-if="visible"
    ref="fab"
    class="permissions-mock-fab"
    :style="style"
    data-testid="groupMockPermissions"
  >
    <VFab
      extended
      color="#000000"
      data-testid="buttonMockPermissions"
      @click="onFabClick"
    >
      <VIcon icon="admin_panel_settings" />
      Mock permissions
    </VFab>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, useTemplateRef } from 'vue';
import { useDraggable } from '@vueuse/core';
import { VFab } from 'vuetify/components/VFab';
import { VIcon } from 'vuetify/components/VIcon';
import { usePermissionsEditDrawer } from '@/composables/usePermissionsEditDrawer';
import { usePermissionsStore } from '@/composables/usePermissionsStore';
import { useUserStore } from '@/stores/userStore';
import { PERMISSION_GRANT_KEYS, type PermissionDraftMap } from '@/types/permissionModules';
import type { PermissionsGrants } from '@/types/permissionsGrants';
import { isGranularPermissionsEnabled } from '@/utils/granularPermissionsFlag';

const EDGE = 24;
const FALLBACK_WIDTH = 196;

function topCenter(width: number) {
  return {
    x: Math.max(EDGE, (window.innerWidth - width) / 2),
    y: EDGE,
  };
}

const userStore = useUserStore();
const { permissions } = usePermissionsStore();
const { open } = usePermissionsEditDrawer();
const fab = useTemplateRef<HTMLElement>('fab');

/**
 * Temporary dev override for GM-3801. Remove it in the permissions cleanup milestone.
 * Vite replaces `import.meta.env.DEV`, so this stays unrendered outside local dev.
 */
const visible = computed(() => import.meta.env.DEV && isGranularPermissionsEnabled());

let suppressClick = false;
let dragOrigin = topCenter(FALLBACK_WIDTH);

const { x, y, style } = useDraggable(fab, {
  initialValue: topCenter(FALLBACK_WIDTH),
  containerElement: document.documentElement,
  onStart: () => {
    dragOrigin = {
      x: x.value,
      y: y.value,
    };
  },
  onEnd: (position) => {
    suppressClick = position.x !== dragOrigin.x || position.y !== dragOrigin.y;
  },
});

onMounted(() => {
  const width = fab.value?.offsetWidth;
  if (!width) return;

  const next = topCenter(width);
  x.value = next.x;
  y.value = next.y;
});

function draftFromGrants(grants: PermissionsGrants): PermissionDraftMap {
  const draft: PermissionDraftMap = {};

  for (const key of PERMISSION_GRANT_KEYS) {
    if (key === 'viewRoster') continue;
    if (grants[key]) draft[key] = true;
  }

  return draft;
}

function onFabClick() {
  if (suppressClick) {
    suppressClick = false;
    return;
  }

  const user = userStore.user;
  if (!user) return;

  open(
    [{
      twrn: user.twrn,
      firstName: user.firstName,
      fullName: user.fullName,
      email: user.email,
      avatar: user.avatar,
      isSuperAdmin: user.isSuperAdmin,
    }],
    {
      readOnly: false,
      useMockPermissions: true,
      draft: draftFromGrants(permissions.value),
    },
  );
}
</script>

<style scoped>
.permissions-mock-fab {
  position: fixed;
  z-index: 1100;
  touch-action: none;
  user-select: none;
}

.permissions-mock-fab :deep(.v-btn) {
  color: #fff !important;
  cursor: grab;
}

.permissions-mock-fab:active :deep(.v-btn) {
  cursor: grabbing;
}
</style>
